import { Platform } from 'react-native';
import * as ImageManipulator from 'expo-image-manipulator';

const MAX_DIMENSION = 1920;
const QUALITY = 0.8;

interface CompressedImage {
  uri: string;
  mimeType: string;
}

/**
 * Get image dimensions from a URI
 */
async function getImageDimensions(uri: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (Platform.OS === 'web') {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = uri;
    } else {
      // On native, we'll get dimensions from ImageManipulator
      ImageManipulator.manipulateAsync(uri, [], { format: ImageManipulator.SaveFormat.JPEG })
        .then((result) => resolve({ width: result.width, height: result.height }))
        .catch(reject);
    }
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 */
function calculateResizeDimensions(
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number } | null {
  if (width <= maxDimension && height <= maxDimension) {
    return null; // No resize needed
  }

  if (width > height) {
    return {
      width: maxDimension,
      height: Math.round((height / width) * maxDimension),
    };
  } else {
    return {
      width: Math.round((width / height) * maxDimension),
      height: maxDimension,
    };
  }
}

/**
 * Convert image to WebP on web platform using Canvas API
 */
async function convertToWebPOnWeb(uri: string, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);

      // Convert to WebP
      const webpDataUrl = canvas.toDataURL('image/webp', quality);
      resolve(webpDataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = uri;
  });
}

/**
 * Compress image for upload
 * - Resizes if larger than MAX_DIMENSION
 * - On web: converts to WebP
 * - On native: uses JPEG compression
 */
export async function compressImage(uri: string): Promise<CompressedImage> {
  try {
    // Get original dimensions
    const dimensions = await getImageDimensions(uri);
    const newDimensions = calculateResizeDimensions(
      dimensions.width,
      dimensions.height,
      MAX_DIMENSION
    );

    if (Platform.OS === 'web') {
      // Web: Use ImageManipulator for resize, then Canvas for WebP conversion
      let processedUri = uri;

      if (newDimensions) {
        // Resize if needed
        const resized = await ImageManipulator.manipulateAsync(
          uri,
          [{ resize: newDimensions }],
          { format: ImageManipulator.SaveFormat.PNG } // Use PNG as intermediate for lossless
        );
        processedUri = resized.uri;
      }

      // Convert to WebP using Canvas API
      const webpUri = await convertToWebPOnWeb(processedUri, QUALITY);

      return {
        uri: webpUri,
        mimeType: 'image/webp',
      };
    } else {
      // Native (iOS/Android): Use ImageManipulator with JPEG compression
      const actions: ImageManipulator.Action[] = [];

      if (newDimensions) {
        actions.push({ resize: newDimensions });
      }

      const result = await ImageManipulator.manipulateAsync(
        uri,
        actions,
        {
          format: ImageManipulator.SaveFormat.JPEG,
          compress: QUALITY,
        }
      );

      return {
        uri: result.uri,
        mimeType: 'image/jpeg',
      };
    }
  } catch (error) {
    console.error('Image compression error:', error);
    // Return original image if compression fails
    return {
      uri,
      mimeType: 'image/jpeg',
    };
  }
}

/**
 * Convert a data URL to a Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/webp';
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}
